import React from 'react';
import styled from 'styled-components';

import type { Theme } from 'app/providers/theme';
import Typography from 'app/ui/components/atoms/typography';

import type {
  Exercise,
  LessonSection,
  SectionExercise,
  SectionExerciseGroup,
  LessonSection as WorkoutSection,
} from 'app/types/graphql';

import { getLessonSectionDescription, indexToLetter } from 'app/pages/workout/util';
import type { HandleSectionFocusChangeProps } from 'app/pages/workout';
import { lengthFromDurationSeconds, lengthFromExercise } from 'app/utils/duration-handler';
import SectionFormatIconWithTooltip from 'app/ui/components/atoms/icons/section-formats/icon-with-tooltip';

import ContentItemAreasWrapper, {
  type ContentData,
  type ContentItemData,
  type GroupContentItemData,
} from 'app/ui/components/molecules/content-item-areas-wrapper';

const MobileSectionDuration = styled(Typography)`
  margin-bottom: 1.5rem;
  weight: bold;
`;

type SectionExerciseToContentItemDataArgs = {
  exercisePosition: string;
  sectionExercise: SectionExercise;
  selectedAlternatives?: Record<string, string>;
  alternativeExercisesEnabled?: boolean;
};

const exerciseToContentItemData = (exercise: Exercise) => ({
  title: exercise.name,
  originalExerciseId: exercise.id,
  content: exercise.description,
  video: exercise.video,
});

const sectionExerciseToContentItemData = (sectionExercise: SectionExercise) => ({
  extraContent: sectionExercise.description,
  additionalInfo: lengthFromExercise(sectionExercise),
  notes: sectionExercise.notes?.trim(),
  rpe: sectionExercise?.rpe,
});

const getContentItemDataFromSectionExercise = ({
  sectionExercise,
  selectedAlternatives,
  alternativeExercisesEnabled = true,
}: SectionExerciseToContentItemDataArgs): ContentItemData => {
  const alternativeExercises =
    sectionExercise?.exercise?.alternativeExercises?.map((exercise) => ({
      id: exercise.id,
      ...sectionExerciseToContentItemData(sectionExercise),
      ...exerciseToContentItemData(exercise),
    })) || [];

  const selectedExerciseId = selectedAlternatives?.[sectionExercise.id];

  if (selectedExerciseId && alternativeExercises.length > 0) {
    const selectedExercise = alternativeExercises.find((exercise) => exercise.id === selectedExerciseId);

    if (selectedExercise) {
      return {
        ...selectedExercise,
        alternativeExercises: alternativeExercisesEnabled ? alternativeExercises : [],
        id: sectionExercise.id,
      };
    }
  }

  return {
    id: sectionExercise.id,
    ...sectionExerciseToContentItemData(sectionExercise),
    ...exerciseToContentItemData(sectionExercise.exercise),
    alternativeExercises: alternativeExercisesEnabled ? alternativeExercises : [],
  };
};

type SectionExerciseGroupToContentItemDataArgs = {
  position: number;
  sectionExerciseGroup: SectionExerciseGroup;
  selectedAlternatives?: Record<string, string>;
  alternativeExercisesEnabled?: boolean;
};

const sectionExerciseGroupToContentItemData = ({
  sectionExerciseGroup,
  selectedAlternatives,
  alternativeExercisesEnabled = true,
}: SectionExerciseGroupToContentItemDataArgs): GroupContentItemData => {
  const currentGroupExercises =
    sectionExerciseGroup?.sectionExercises?.map((sectionExercise, index) =>
      getContentItemDataFromSectionExercise({
        sectionExercise,
        exercisePosition: indexToLetter(index),
        selectedAlternatives,
        alternativeExercisesEnabled,
      }),
    ) || [];

  const nestedGroups =
    sectionExerciseGroup?.sectionExerciseGroups?.map((nestedGroup, index) =>
      sectionExerciseGroupToContentItemData({
        sectionExerciseGroup: nestedGroup,
        position: index + 1,
        selectedAlternatives,
        alternativeExercisesEnabled,
      }),
    ) || [];

  const nestedGroupExercises = nestedGroups.flatMap((group) => group.sectionExercises);

  return {
    title: sectionExerciseGroup.name,
    content: sectionExerciseGroup.description,
    additionalInfo: lengthFromDurationSeconds(sectionExerciseGroup.duration || 0),
    id: sectionExerciseGroup.id,
    showTitle: sectionExerciseGroup.showTitle,
    sectionExercises: [...currentGroupExercises, ...nestedGroupExercises],
    nestedGroups: nestedGroups.length > 0 ? nestedGroups : undefined,
    notes: sectionExerciseGroup?.notes?.trim(),
    template: sectionExerciseGroup?.sectionExerciseGroupTemplate,
  };
};

type WorkoutDataProps = {
  contentItemFocused?: string | null;
  handleContentItemClick?: (prop: HandleSectionFocusChangeProps) => void;
  isMobile?: boolean;
  theme?: Theme;
  dataOnly?: boolean;
  selectedAlternatives?: Record<string, string>;
};

type SectionsToDataArgs = WorkoutDataProps & {
  lessonSections: LessonSection[];
};

type SectionToDataArgs = WorkoutDataProps & {
  lessonSection: WorkoutSection;
  alternativeExercisesEnabled?: boolean;
  expandNotesTextByDefault?: boolean;
  hideExerciseChevrons?: boolean;
};

export const sectionToData = ({
  lessonSection,
  theme,
  isMobile = false,
  dataOnly = false,
  contentItemFocused = null,
  handleContentItemClick = () => undefined,
  selectedAlternatives = {},
  alternativeExercisesEnabled = true,
  expandNotesTextByDefault = false,
  hideExerciseChevrons = false,
}: SectionToDataArgs): ContentData => {
  // one-fiit can hold a section's exercises DIRECTLY on the section, a shape hyrox-api could not
  // express: importHyroxLesson flattens any hyrox section holding exactly one group onto the
  // section itself, which is most warm-ups, cooldowns and finishers (migration finding Q35).
  const directExercises =
    lessonSection?.sectionExercises?.map((sectionExercise, index) =>
      getContentItemDataFromSectionExercise({
        sectionExercise,
        exercisePosition: indexToLetter(index),
        selectedAlternatives,
        alternativeExercisesEnabled,
      }),
    ) || [];

  const groups = lessonSection?.sectionExerciseGroups || [];

  // The cutover may have adopted the flattened section's now-empty hyrox group beside those
  // exercises. That shell still carries the group's title and description, so fold the direct
  // exercises back into it rather than rendering an empty group next to a headless list.
  const emptyGroupIndex =
    directExercises.length > 0
      ? groups.findIndex((group) => !group.sectionExercises?.length && !group.sectionExerciseGroups?.length)
      : -1;

  const groupsData = groups
    .map((sectionExerciseGroup, index) =>
      sectionExerciseGroupToContentItemData({
        sectionExerciseGroup,
        position: index + 1,
        selectedAlternatives,
        alternativeExercisesEnabled,
      }),
    )
    .map((groupData, index) =>
      index === emptyGroupIndex ? { ...groupData, sectionExercises: directExercises } : groupData,
    );

  // No shell to fold into (the shape prod gets post-cutover): render them as one untitled group.
  const sectionExerciseGroupsData =
    directExercises.length > 0 && emptyGroupIndex === -1
      ? [
          {
            id: `${lessonSection.id}_direct`,
            title: '',
            additionalInfo: '',
            showTitle: false,
            sectionExercises: directExercises,
          },
          ...groupsData,
        ]
      : groupsData;

  const duration = lengthFromDurationSeconds(lessonSection.duration);

  return {
    title: lessonSection.name,
    titleExtraContent: (
      <SectionFormatIconWithTooltip format={lessonSection.format} isRotational={lessonSection.isRotational} />
    ),
    content:
      isMobile && !dataOnly ? (
        <>
          <MobileSectionDuration variant="body2" color={theme?.colors.paragraphGray}>
            Section duration: <b>{duration.toLowerCase()}</b>
          </MobileSectionDuration>
          {getLessonSectionDescription(lessonSection)}
        </>
      ) : (
        getLessonSectionDescription(lessonSection)
      ),
    notes: lessonSection.notes?.trim(),
    duration: lessonSection.duration,
    additionalInfo: duration,
    format: lessonSection.format,
    workTime: lessonSection.workTime,
    restTime: lessonSection.restTime,
    rounds: lessonSection.rounds,
    id: lessonSection.id,
    expandTextByDefault: expandNotesTextByDefault,
    ...(dataOnly
      ? { groups: sectionExerciseGroupsData }
      : {
          contentItems: (
            <ContentItemAreasWrapper
              items={sectionExerciseGroupsData}
              contentItemFocused={contentItemFocused}
              handleContentItemClick={handleContentItemClick}
              hideExerciseChevrons={hideExerciseChevrons}
            />
          ),
        }),
  };
};

export const sectionsToData = ({
  lessonSections = [],
  contentItemFocused,
  handleContentItemClick,
  isMobile,
  theme,
  dataOnly = false,
  selectedAlternatives = {},
}: SectionsToDataArgs): ContentData[] =>
  lessonSections.map((lessonSection) =>
    sectionToData({
      lessonSection,
      contentItemFocused,
      handleContentItemClick,
      isMobile,
      theme,
      dataOnly,
      selectedAlternatives,
    }),
  );
